import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Disc3, Loader2, Plus, RefreshCw } from "lucide-react";
import AppBackControl from "../components/ui/AppBackControl";
import BondWheelCard from "../components/bond-wheel/BondWheelCard";
import CreateBondWheelModal from "../components/bond-wheel/CreateBondWheelModal";
import BondWheelJoinModal from "../components/bond-wheel/BondWheelJoinModal";
import BondPlayStageHelpButton from "../components/bond-play/BondPlayStageHelpButton";
import BondWheelInfoModal from "../components/bond-play/BondWheelInfoModal";
import { selectAppUser } from "../redux/appAuthSlice";
import {
  bondWheelCreate,
  bondWheelJoin,
  fetchBondWheelConstants,
  fetchBondWheelGames,
  selectBondWheelActionLoading,
  selectBondWheelConstants,
  selectBondWheelGames,
  selectBondWheelLoading,
} from "../redux/bondWheelSlice";
import {
  formatCoin,
  isUserCreator,
  isUserInGame,
  makeJoinIdempotencyKey,
} from "../utils/bondWheelHelpers";
import { initBondWheelSocket } from "../redux/bondWheelSocket";
import { store } from "../../redux/store";
import "../bond-wheel.css";

const FILTERS = [
  { key: "live", label: "Live" },
  { key: "mine", label: "Mine" },
  { key: "open", label: "Open" },
  { key: "all", label: "All" },
];

const BondWheel = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectAppUser);
  const userId = user?._id;
  const userCoin = Number(user?.COIN || 0);
  const games = useSelector(selectBondWheelGames);
  const loading = useSelector(selectBondWheelLoading);
  const actionLoading = useSelector(selectBondWheelActionLoading);
  const constants = useSelector(selectBondWheelConstants);

  const [filter, setFilter] = useState("live");
  const [createOpen, setCreateOpen] = useState(false);
  const [joinConfirmGame, setJoinConfirmGame] = useState(null);
  const [busyGameId, setBusyGameId] = useState(null);
  const [toast, setToast] = useState(null);
  const [wheelInfoOpen, setWheelInfoOpen] = useState(false);

  useEffect(() => {
    initBondWheelSocket(dispatch, store.getState);
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchBondWheelGames({ limit: 60 }));
    if (!constants) dispatch(fetchBondWheelConstants());
  }, [dispatch, constants]);

  useEffect(() => {
    const hasOverdueLive = (games || []).some((game) => {
      if (game.status !== "Active" && game.status !== "Locked") return false;
      const deadlineMs = new Date(game.deadline).getTime();
      return Number.isFinite(deadlineMs) && deadlineMs <= Date.now();
    });
    if (!hasOverdueLive) return undefined;

    dispatch(fetchBondWheelGames({ limit: 60 }));
    dispatch(fetchBondWheelGames({ status: "Settled", limit: 100 }));
    const interval = setInterval(() => {
      dispatch(fetchBondWheelGames({ limit: 60 }));
      dispatch(fetchBondWheelGames({ status: "Settled", limit: 100 }));
    }, 5000);
    return () => clearInterval(interval);
  }, [dispatch, games]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const filteredGames = useMemo(() => {
    const list = (games || []).slice();
    list.sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
    return list.filter((game) => {
      if (filter === "all") return true;
      if (filter === "live") return game.status === "Active" || game.status === "Locked";
      if (filter === "mine") return isUserInGame(game, userId) || isUserCreator(game, userId);
      if (filter === "open") return game.status === "Active";
      return true;
    });
  }, [games, filter, userId]);

  const stats = useMemo(() => {
    const liveGames = (games || []).filter((g) => g.status === "Active" || g.status === "Locked");
    const myGames = (games || []).filter((g) => isUserInGame(g, userId) || isUserCreator(g, userId));
    const totalPot = liveGames.reduce((sum, g) => sum + Number(g.totalPot || 0), 0);
    return { live: liveGames.length, mine: myGames.length, totalPot };
  }, [games, userId]);

  const handleRefresh = () => {
    dispatch(fetchBondWheelGames({ limit: 60 }));
  };

  const handleCreate = async ({ entryAmount, deadline, numberOfWinners, minimumPlayers }) => {
    const createIdempotencyKey = `bw-create-${userId}-${Date.now()}`;
    const res = await dispatch(
      bondWheelCreate({
        entryAmount,
        deadline,
        numberOfWinners,
        minimumPlayers,
        createIdempotencyKey,
      })
    ).unwrap();

    if (!res?.game?._id) {
      throw new Error("Server did not return a valid game. Please try again.");
    }

    setToast(res?.reused ? "Your existing wheel is ready." : "Bond Wheel created.");
    navigate(`/app/bond-play/bond-wheel/${res.game._id}`);
    return res;
  };

  const executeJoin = async (game, ticketCount = 1) => {
    if (!game?._id) return;
    setBusyGameId(game._id);
    try {
      await dispatch(
        bondWheelJoin({
          gameId: game._id,
          ticketCount,
          joinIdempotencyKey: makeJoinIdempotencyKey(userId, game._id),
        })
      ).unwrap();
      setJoinConfirmGame(null);
      setToast(ticketCount > 1 ? `${ticketCount} tickets added.` : "Ticket added.");
      navigate(`/app/bond-play/bond-wheel/${game._id}`);
    } catch (e) {
      setToast(e?.message || "Could not join. Please try again.");
    } finally {
      setBusyGameId(null);
    }
  };

  const goToPlayground = useCallback(
    (game) => {
      if (!game?._id) return;
      navigate(`/app/bond-play/bond-wheel/${game._id}`);
    },
    [navigate]
  );

  const openMyPlayground = () => {
    const playable = (games || [])
      .filter(
        (g) =>
          (g.status === "Active" || g.status === "Locked") &&
          (isUserInGame(g, userId) || isUserCreator(g, userId))
      )
      .sort((a, b) => new Date(a.deadline) - new Date(b.deadline));

    if (playable.length === 0) {
      setToast("Create or join a live wheel first.");
      return;
    }
    goToPlayground(playable[0]);
  };

  return (
    <div className="bw-page bw-list-page">
      <div className="bw-page-header">
        <div>
          <AppBackControl to="/app/bond-play" label="Play" />
          <h1 className="bw-page-title">Bond Wheel</h1>
          <p className="bw-page-sub">Set winners and minimum players — split the pot or refund everyone.</p>
        </div>
        <div className="bw-header-actions">
          <BondPlayStageHelpButton
            helpLabel="How Bond Wheel works"
            onOpenInfo={() => setWheelInfoOpen(true)}
            className="bw-header-help"
          />
          <button type="button" className="bw-btn" onClick={handleRefresh} disabled={loading}>
            <RefreshCw size={14} className={loading ? "bw-spin-icon" : ""} />
            Refresh
          </button>
          <button type="button" className="bw-btn bw-btn-primary" onClick={() => setCreateOpen(true)}>
            <Plus size={14} />
            Create
          </button>
        </div>
      </div>

      <div className="bw-hero-strip">
        <div className="bw-hero-icon">
          <Disc3 size={18} color="#64B5F6" />
        </div>
        <div className="bw-hero-stats">
          <span>{stats.live} live</span>
          <span>{stats.mine} mine</span>
          <span className="bw-hero-gold">{formatCoin(stats.totalPot)} pot</span>
        </div>
      </div>

      <div className="bw-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`bw-filter ${filter === f.key ? "active" : ""}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && filteredGames.length === 0 ? (
        <div className="bw-empty-state">
          <Loader2 size={24} className="bw-spin-icon" />
          <p>Loading wheels…</p>
        </div>
      ) : filteredGames.length === 0 ? (
        <div className="bw-empty-state">
          <Disc3 size={32} color="#64B5F6" />
          <h2>No wheels here yet</h2>
          <p>
            {filter === "mine"
              ? "You haven't joined or created any wheels."
              : "Be the first to spin one up."}
          </p>
          <button type="button" className="bw-btn bw-btn-primary" onClick={() => setCreateOpen(true)}>
            Create a wheel
          </button>
        </div>
      ) : (
        <div className="bw-grid">
          {filteredGames.map((game) => (
            <BondWheelCard
              key={game._id}
              game={game}
              userId={userId}
              onOpen={goToPlayground}
              onJoin={setJoinConfirmGame}
              joining={busyGameId === game._id && actionLoading}
            />
          ))}
        </div>
      )}

      <div className="bw-list-footer">
        <button type="button" className="bw-btn" onClick={() => navigate("/app/bond-play")}>
          Back to Play
        </button>
        <button type="button" className="bw-btn bw-btn-primary" onClick={openMyPlayground}>
          Open my wheel
        </button>
      </div>

      {toast ? <div className="bw-toast">{toast}</div> : null}

      <CreateBondWheelModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        constants={constants}
        userCoinBalance={userCoin}
        submitting={actionLoading}
      />

      <BondWheelJoinModal
        open={!!joinConfirmGame}
        game={joinConfirmGame}
        userId={userId}
        userCoinBalance={userCoin}
        maxTicketsPerJoin={constants?.maxTicketsPerJoin}
        loading={!!joinConfirmGame && busyGameId === joinConfirmGame._id}
        onClose={() => !busyGameId && setJoinConfirmGame(null)}
        onConfirm={(ticketCount) => executeJoin(joinConfirmGame, ticketCount)}
      />

      <BondWheelInfoModal
        open={wheelInfoOpen}
        onClose={() => setWheelInfoOpen(false)}
        constants={constants}
      />
    </div>
  );
};

export default BondWheel;
